import { Injectable } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { translateApiError } from 'src/app/utils/api-error';

/**
 * Para peticiones de precarga "best effort" (p. ej. datos que se muestran
 * más adelante en un asistente y de los que el usuario actual puede no tener
 * permiso todavía): evita el popup global de "Acceso denegado" en un 403,
 * dejando que el propio `subscribe({ error })` del componente lo gestione en
 * silencio. Uso: `this.http.get(url, { context: withSilentForbidden() })`.
 */
export const SKIP_FORBIDDEN_ALERT = new HttpContextToken<boolean>(() => false);

export function withSilentForbidden(context = new HttpContext()): HttpContext {
  return context.set(SKIP_FORBIDDEN_ALERT, true);
}

/**
 * Respuestas 403 `trial_locked` (back #20261023-090009: la prueba de la empresa
 * terminó y la API del dashboard se cierra salvo Plan). Las escucha
 * `CorporationTrialService` para pintar la capa de bloqueo, que es el aviso:
 * el interceptor no saca otro (#20261023-090010, CR6).
 */
export const trialLockedResponses$ = new Subject<void>();

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private translate: TranslateService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((raw: HttpErrorResponse) => {
        // Textos del error en el idioma del dashboard a partir de su código
        // (error_type, code de autenticación, failed_rules). Sin traducción se
        // queda el `message` del servidor. Ver src/app/utils/api-error.ts.
        const error = targetsOwnApi(req.url) ? translateApiError(this.translate, raw) : raw;
        const silentForbidden = req.context.get(SKIP_FORBIDDEN_ALERT);
        if (error.status === 401 && req.url.includes('/me')) {
          this.handleUnauthorizedMe();
        } else if (error.status === 403 && req.url.includes('/me')) {
          this.handleForbiddenError(error.error?.message);
        } else if (error.status === 403 && error.error?.error_type === 'trial_locked') {
          // La capa de bloqueo es el aviso: ningún modal por cada pantalla cerrada.
          trialLockedResponses$.next();
        } else if (error.status === 403 && !silentForbidden) {
          // `feature_locked`: no es falta de permiso, sino que el plan de la
          // empresa no incluye la función. El resto de 403 (p. ej.
          // AUTH_BACKOFFICE_PERMISSION_DENIED) usan el aviso de acceso denegado.
          if (error.error?.error_type === 'feature_locked') {
            this.handleFeatureLocked();
          } else {
            this.handleForbiddenError();
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private handleUnauthorizedMe(): void {
    // Limpiar datos de sesión
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');

    // Redirigir al login
    this.router.navigate(['/auth/login']);
  }

  private handleFeatureLocked(): void {
    Swal.fire({
      icon: 'info',
      title: this.translate.instant('HTTP_ERRORS.FEATURE_LOCKED.TITLE'),
      text: this.translate.instant('HTTP_ERRORS.FEATURE_LOCKED.MESSAGE'),
      confirmButtonText: this.translate.instant('HTTP_ERRORS.FORBIDDEN.CONFIRM'),
    });
  }

  private handleForbiddenError(serverMessage?: string): void {
    Swal.fire({
      icon: 'error',
      title: this.translate.instant('HTTP_ERRORS.FORBIDDEN.TITLE'),
      text: serverMessage || this.translate.instant('HTTP_ERRORS.FORBIDDEN.MESSAGE'),
      confirmButtonText: this.translate.instant(
        'HTTP_ERRORS.FORBIDDEN.CONFIRM',
      ),
      confirmButtonColor: '#dc3545',
    });
  }
}

/** Solo las respuestas de nuestro backend llevan los códigos del catálogo. */
function targetsOwnApi(url: string): boolean {
  return [environment.laravelApi, environment.laravelBackoffice].some(
    (base) => !!base && url.startsWith(base),
  );
}
